import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, Btn, Input, Select, Field, Modal } from "@/components/ui-kit";
import { KANBAN_COLUMNS, BRANCHES, CLASSIFICATIONS, findMeta, fmtMoney, branchColor } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Flag, GripVertical, Trash2, LayoutGrid, GanttChartSquare } from "lucide-react";

export default function ProjectBoard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [proj, setProj] = useState(null);
  const [techs, setTechs] = useState([]);
  const [view, setView] = useState("kanban");
  const [addOpen, setAddOpen] = useState(false);
  const [drag, setDrag] = useState(null);

  const load = useCallback(() => { api.get(`/projects/${id}`).then((r) => setProj(r.data)).catch(() => toast.error("Not found")); }, [id]);
  useEffect(() => { load(); api.get("/users").then((r) => setTechs(r.data.filter((u) => u.role === "technician"))); }, [load]);

  if (!proj) return <div className="text-sm text-muted-foreground">Loading project board…</div>;
  const canEdit = can("project.manage");
  const canDelete = can("records.delete");
  const clazz = findMeta(CLASSIFICATIONS, proj.classification_level);
  const techName = (tid) => (techs.find((t) => t.id === tid) || {}).full_name || "Unassigned";

  const moveTask = async (task, status) => {
    if (task.status === status) return;
    setProj({ ...proj, tasks: proj.tasks.map((t) => (t.id === task.id ? { ...t, status } : t)) });
    try { await api.put(`/tasks/${task.id}`, { status }); } catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };
  const del = async (tid) => { await api.delete(`/tasks/${tid}`); load(); };

  const totalBudget = proj.tasks.reduce((s, t) => s + (t.budgeted_cost || 0), 0);
  const totalActual = proj.tasks.reduce((s, t) => s + (t.actual_cost || 0), 0);

  return (
    <div className="space-y-4" data-testid="project-board-page">
      <button onClick={() => navigate("/projects")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Projects</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] text-muted-foreground">{proj.project_code}</div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">{proj.title}</h1>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <Badge className="border-border" style={{ color: branchColor(proj.client_branch) }}>{findMeta(BRANCHES, proj.client_branch).label}</Badge>
            <Badge className={clazz.cls}>{clazz.label}</Badge>
            <Badge className="border-border text-muted-foreground">{proj.status}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border p-0.5">
            <button onClick={() => setView("kanban")} data-testid="view-kanban" className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold ${view === "kanban" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><LayoutGrid size={14} /> Kanban</button>
            <button onClick={() => setView("gantt")} data-testid="view-gantt" className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold ${view === "gantt" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><GanttChartSquare size={14} /> Gantt</button>
          </div>
          {canEdit && <Btn onClick={() => setAddOpen(true)} data-testid="add-task-btn"><Plus size={16} /> Task</Btn>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Tasks" value={proj.tasks.length} />
        <Stat label="Budgeted" value={fmtMoney(totalBudget)} />
        <Stat label="Actual Spend" value={fmtMoney(totalActual)} />
        <Stat label="Linked Jobs" value={proj.jobs.length} />
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {KANBAN_COLUMNS.map((col) => {
            const tasks = proj.tasks.filter((t) => t.status === col.value);
            return (
              <div key={col.value} data-testid={`kanban-col-${col.value}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (drag) moveTask(drag, col.value); setDrag(null); }}
                className="flex flex-col rounded-lg border border-border bg-card/60">
                <div className="flex items-center justify-between border-b border-border px-3 py-2">
                  <span className="font-heading text-xs font-bold uppercase tracking-wide">{col.label}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{tasks.length}</span>
                </div>
                <div className="flex-1 space-y-2 p-2">
                  {tasks.map((t) => (
                    <div key={t.id} draggable onDragStart={() => setDrag(t)} data-testid={`task-${t.id}`}
                      className="group rounded-md border border-border bg-card p-2.5 text-sm shadow-sm hover:border-primary/50">
                      <div className="flex items-start gap-1.5">
                        <GripVertical size={13} className="mt-0.5 shrink-0 cursor-grab text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1 font-medium">{t.is_milestone && <Flag size={12} className="text-amber-500" />}{t.title}</div>
                          <div className="mt-1 font-mono text-[10px] text-muted-foreground">{techName(t.assigned_technician_id)}</div>
                          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full bg-primary" style={{ width: `${t.progress || 0}%` }} />
                          </div>
                          <div className="mt-1 flex items-center justify-between font-mono text-[10px] text-muted-foreground">
                            <span>{t.progress || 0}%</span><span>{fmtMoney(t.actual_cost)}/{fmtMoney(t.budgeted_cost)}</span>
                          </div>
                        </div>
                        {canDelete && <button onClick={() => del(t.id)} data-testid={`del-task-${t.id}`} className="opacity-0 transition group-hover:opacity-100"><Trash2 size={13} className="text-red-500" /></button>}
                      </div>
                    </div>
                  ))}
                  {tasks.length === 0 && <div className="py-4 text-center font-mono text-[10px] text-muted-foreground">drop here</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Gantt tasks={proj.tasks} branch={proj.client_branch} techName={techName} />
      )}

      {addOpen && <TaskForm projectId={id} techs={techs} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value }) {
  return <Card className="p-3"><div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-mono text-lg font-bold">{value}</div></Card>;
}

function Gantt({ tasks, branch, techName }) {
  const dated = tasks.filter((t) => t.start_date && t.end_date);
  if (dated.length === 0) return <Card className="p-6 text-center text-sm text-muted-foreground">Add start & end dates to tasks to render the Gantt timeline.</Card>;
  const starts = dated.map((t) => new Date(t.start_date).getTime());
  const ends = dated.map((t) => new Date(t.end_date).getTime());
  const min = Math.min(...starts), max = Math.max(...ends);
  const span = Math.max(1, max - min);
  const color = branchColor(branch);
  return (
    <Card className="overflow-x-auto p-4" data-testid="gantt-chart">
      <div className="min-w-[640px] space-y-2">
        {dated.map((t) => {
          const left = ((new Date(t.start_date).getTime() - min) / span) * 100;
          const width = Math.max(4, ((new Date(t.end_date).getTime() - new Date(t.start_date).getTime()) / span) * 100);
          return (
            <div key={t.id} className="flex items-center gap-3 text-sm">
              <div className="w-52 shrink-0 truncate">
                <div className="flex items-center gap-1 font-medium">{t.is_milestone && <Flag size={12} className="text-amber-500" />}{t.title}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{techName(t.assigned_technician_id)}</div>
              </div>
              <div className="relative h-6 flex-1 rounded bg-muted/60">
                <div className="absolute top-0 flex h-6 items-center justify-end rounded px-1.5 font-mono text-[9px] font-bold text-white"
                  style={{ left: `${left}%`, width: `${width}%`, background: color }}>
                  <div className="absolute inset-y-0 left-0 rounded bg-black/25" style={{ width: `${t.progress || 0}%` }} />
                  <span className="relative">{t.progress || 0}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TaskForm({ projectId, techs, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", status: "backlog", assigned_technician_id: "", budgeted_cost: 0, actual_cost: 0, start_date: "", end_date: "", is_milestone: false, progress: 0 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    try {
      await api.post("/tasks", { project_id: projectId, ...f, budgeted_cost: Number(f.budgeted_cost), actual_cost: Number(f.actual_cost), progress: Number(f.progress), assigned_technician_id: f.assigned_technician_id || null });
      toast.success("Task added"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title="New WBS Task">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Title" required><Input data-testid="tf-title" value={f.title} onChange={set("title")} /></Field>
        <Field label="Column"><Select value={f.status} onChange={set("status")}>{KANBAN_COLUMNS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</Select></Field>
        <Field label="Technician"><Select value={f.assigned_technician_id} onChange={set("assigned_technician_id")}><option value="">—</option>{techs.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}</Select></Field>
        <Field label="Progress %"><Input type="number" min={0} max={100} value={f.progress} onChange={set("progress")} /></Field>
        <Field label="Budgeted ₹"><Input type="number" value={f.budgeted_cost} onChange={set("budgeted_cost")} /></Field>
        <Field label="Actual ₹"><Input type="number" value={f.actual_cost} onChange={set("actual_cost")} /></Field>
        <Field label="Start Date"><Input type="date" value={f.start_date} onChange={set("start_date")} /></Field>
        <Field label="End Date"><Input type="date" value={f.end_date} onChange={set("end_date")} /></Field>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_milestone} onChange={(e) => setF({ ...f, is_milestone: e.target.checked })} /> Mark as milestone</label>
      <div className="mt-4 flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="task-submit-btn">Add Task</Btn></div>
    </Modal>
  );
}
